import { z } from 'zod';
import { PAGINATION_SORT_DIRECTION } from '~/constants';

import { zodAddressSchema } from '@vestigas/data';

import { EmailSchema, toSnakeCase } from '@vestigas/utils';

import { INVOICES_DIRECTION, INVOICES_ORDER_BY } from './constants';

/**
 * Validation schemas for invoice-related API calls.
 */

const INVOICE_QUERY_TEXT_FILTER_MAX_LENGTH = 255;

// Query parameter validations
export const zodInvoiceDirectionSchema = z.enum(INVOICES_DIRECTION);
const zodInvoiceCheckStatusSchema = z.enum([
  'correct',
  'formal_check_failed',
  'no_check_possible',
  'dln_not_found',
  'dln_not_signed',
  'dln_check_failed',
  'price_check_failed',
]);
const zodIsoDatetimeWithOptionalTimezoneSchema = z.union([
  z.iso.datetime(),
  z.iso.datetime({ offset: true }),
]);
const zodDecimalValueSchema = z.union([z.string(), z.number()]);

export const zodInvoicesQueryParamsSchema = z.object({
  accountingReferenceIds: z.array(z.uuid()).optional(),
  buyerIds: z.array(z.uuid()).optional(),
  checkStatus: z.array(zodInvoiceCheckStatusSchema).optional(),
  direction: zodInvoiceDirectionSchema,
  invoiceDateAfter: z.iso.date().optional(),
  invoiceDateBefore: z.iso.date().optional(),
  invoiceDatetimeParsedAfter:
    zodIsoDatetimeWithOptionalTimezoneSchema.optional(),
  invoiceDatetimeParsedBefore:
    zodIsoDatetimeWithOptionalTimezoneSchema.optional(),
  invoiceNr: z.string().max(INVOICE_QUERY_TEXT_FILTER_MAX_LENGTH).optional(),
  limit: z.number().int().min(1).prefault(100),
  offset: z.number().int().min(0).prefault(0),
  orderBy: z
    .preprocess(
      (value) => toSnakeCase(typeof value === 'string' ? value : ''),
      z.enum(INVOICES_ORDER_BY),
    )
    .prefault('date'),
  searchString: z
    .string()
    .trim()
    .max(INVOICE_QUERY_TEXT_FILTER_MAX_LENGTH)
    .optional(),
  sellerIds: z.array(z.uuid()).optional(),
  sortOrder: z
    .preprocess(
      (value) => (typeof value === 'string' ? value.toUpperCase() : undefined),
      z.enum(PAGINATION_SORT_DIRECTION),
    )
    .prefault('DESC'),
  withAccountingReferences: z.boolean().optional(),
});

const zodAccountingReferenceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

const zodCompanySchema = z.object({
  address: zodAddressSchema,
  companyAccountId: z.uuid(),
  division: z.string().nullish(),
  email: EmailSchema.nullish(),
  id: z.uuid(),
  issuerAssignedId: z.string().nullish(),
  name: z.string(),
  phone: z.string().nullish(),
});

export const zodInvoicesListItemSchema = z.object({
  accountingReferences: z.array(zodAccountingReferenceSchema).nullish(),
  buyer: zodCompanySchema,
  currency: z.string().prefault('EUR'),
  date: z.string(),
  datetimeParsed: zodIsoDatetimeWithOptionalTimezoneSchema,
  grossTotal: zodDecimalValueSchema,
  invoiceId: z.uuid(),
  invoiceNr: z.string(),
  seller: zodCompanySchema,
  watermark: z.string().nullish(), // TODO: add watermark enum
});

export const zodInvoicesResponseSchema = z.object({
  data: z.array(zodInvoicesListItemSchema),
  hasNextPage: z.boolean(),
  paginatedCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative().nullable(),
});
